import React, {useState} from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {Trash2, Edit, Shield} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {SettingsToolbar, SelectionBar, SettingsTable, AddAction, ListLoader} from "./SettingsUI";

const ProfilesList = ({
                          searchTerm,
                          setSearchTerm,
                          debouncedSearchTerm,
                          selectedItems,
                          setSelectedItems,
                          selectedAll,
                          setSelectedAll,
                          showNotification,
                          onCreateProfile,
                          onEditProfile
                      }) => {
    const queryClient = useQueryClient();
    // ids awaiting confirmation: the selection, or a single row from its menu
    const [pendingDelete, setPendingDelete] = useState(null);


    // Function to fetch profiles from the API
    const fetchProfiles = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const headers = {
                'X-account-id': accountId.id,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            };

            const response = await api.get('/profils', {headers});

            // Filter profiles by search term if provided
            let filteredProfiles = response.data;
            if (debouncedSearchTerm) {
                const searchLower = debouncedSearchTerm.toLowerCase();
                filteredProfiles = filteredProfiles.filter(profile =>
                    profile.name.toLowerCase().includes(searchLower)
                );
            }

            console.log("filteredProfiles", filteredProfiles);
            return filteredProfiles;
        } catch (error) {
            console.error("Error fetching profiles:", error);
            throw new Error(error.response?.data?.detail || "Erreur lors du chargement des profils");
        }
    };

    // Function to delete profiles
    const deleteProfiles = async (profileIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const headers = {
                'X-account-id': accountId.id,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            };

            // Use Promise.all to handle multiple deletions
            await Promise.all(
                profileIds.map(id =>
                    api.delete(`/profils/${id}`, {headers})
                )
            );

            return {success: true};
        } catch (error) {
            console.error("Error deleting profiles:", error);
            throw new Error(error.response?.data?.detail || "Erreur lors de la suppression des profils");
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteProfiles,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['profiles']});
            showNotification('success', 'Profils supprimés avec succès');
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Use react-query to fetch and cache the data
    const {data: profiles, isLoading, error} = useQuery({
        queryKey: ['profiles', debouncedSearchTerm],
        queryFn: fetchProfiles,
        staleTime: 60000 // 1 minute
    });

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? (profiles || []).map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (profileId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(profileId)
                ? prevSelected.filter(id => id !== profileId)
                : [...prevSelected, profileId]
        );
    };

    // Helper function to count active permissions
    const countActivePermissions = (permissions) => {
        return permissions.filter(perm => {
            // Handle status whether it's boolean or string
            return perm.status === true || perm.status === "true";
        }).length;
    };

    // Helper function to format role name
    const formatRoleName = (name) => {
        if (name.startsWith('ROLE_')) {
            return name.replace('ROLE_', '');
        }
        return name;
    };

    const activePermissions = (profile) =>
        profile.permissions.filter(perm => perm.status === true || perm.status === "true");

    const rowActions = (profile) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditProfile(profile), tone: 'blue'},
        {
            label: 'Supprimer', icon: Trash2, tone: 'red', danger: true,
            onClick: () => setPendingDelete([profile.id]),
        },
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (profile) => (
            <span className="flex items-center gap-2 min-w-0">
                <Shield className="w-4 h-4 flex-shrink-0 text-primary"/>
                <span className="truncate">{formatRoleName(profile.name)}</span>
            </span>
        )},
        {key: 'permissions', label: 'Permissions', render: (profile) => {
            const active = activePermissions(profile);
            return (
                <span className="flex flex-wrap items-center gap-1">
                    <span className="text-gray-500 dark:text-gray-400 mr-1">{countActivePermissions(profile.permissions)} active(s)</span>
                    {active.slice(0, 3).map(perm => (
                        <span key={perm.id} className="hidden md:inline text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            {perm.label}
                        </span>
                    ))}
                    {active.length > 3 && (
                        <span className="hidden md:inline text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                            +{active.length - 3}
                        </span>
                    )}
                </span>
            );
        }},
    ];

    return (
        <div>
            <AddAction onClick={onCreateProfile}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher un profil..."/>

            <SelectionBar count={selectedItems.length} singular="profil sélectionné" plural="profils sélectionnés"
                          onDelete={() => setPendingDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading ? (
                <ListLoader label="Chargement des profils..."/>
            ) : error ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={profiles || []}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditProfile}
                    empty={{
                        icon: Shield,
                        title: debouncedSearchTerm ? 'Aucun profil trouvé' : 'Aucun profil',
                        text: 'Les profils définissent les droits de vos employés.',
                    }}
                />
            )}

            {pendingDelete && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${pendingDelete.length} profil${pendingDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={() => {
                        deleteMutation.mutate(pendingDelete);
                        setPendingDelete(null);
                    }}
                    onCancel={() => setPendingDelete(null)}
                    danger={true}
                />
            )}
        </div>
    );
};

export default ProfilesList;
